import React, { useState } from "react";
import { Modal, Button, Collapse } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKitchenSet, faChevronDown, faChevronUp, faCompressAlt, faExpandAlt } from "@fortawesome/free-solid-svg-icons";
import Fraction from "fraction.js";
import "../../../styles/WeeklyPrepListModal.css";

const WeeklyPrepListModal = ({ show, onHide, prepItems, isMetric, multiplier = 1 }) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "None"];
  
  // Get today's day name
  const today = days[new Date().getDay()];
  
  // Initialize collapsed state for each day that has items
  const initializeCollapsedState = () => {
    const state = {};
    days.forEach(day => {
      if (prepItems[day] && prepItems[day].length > 0) {
        state[day] = false; // Start expanded
      }
    });
    return state;
  };
  
  const [collapsedDays, setCollapsedDays] = useState(initializeCollapsedState);
  const [collapsedPrepTypes, setCollapsedPrepTypes] = useState({});
  
  // Reset collapsed state when modal opens
  React.useEffect(() => {
    if (show) {
      setCollapsedDays(initializeCollapsedState());
      setCollapsedPrepTypes({});
    }
  }, [show, prepItems]);
  
  // Toggle individual day
  const toggleDay = (day) => {
    setCollapsedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };
  
  // Collapse/Expand all days
  const toggleAllDays = (collapse) => {
    const newState = {};
    days.forEach(day => {
      if (prepItems[day] && prepItems[day].length > 0) {
        newState[day] = collapse;
      }
    });
    setCollapsedDays(newState);
  };
  
  // Toggle prep type section
  const togglePrepType = (day, prepType) => {
    const key = `${day}-${prepType}`;
    setCollapsedPrepTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Format quantity for display
  const formatQuantity = (qty) => {
    const result = parseFloat(qty) * multiplier;
    let displayValue;

    if (isMetric && !Number.isInteger(result)) {
      displayValue = result.toFixed(2);
    } else if (!isMetric) {
      if ((result % 1).toFixed(2) >= 0.31 && (result % 1).toFixed(2) <= 0.35) {
        displayValue = new Fraction(Math.round(3 * result)).div(3).toFraction(true);
      } else if ((result % 1).toFixed(2) >= 0.65 && (result % 1).toFixed(2) <= 0.68) {
        displayValue = new Fraction(Math.round(3 * result)).div(3).toFraction(true);
      } else {
        displayValue = new Fraction(Math.round(8 * result)).div(8).toFraction(true);
      }
    } else {
      displayValue = result;
    }

    return displayValue;
  };

  // Check if there are any prep items
  const hasAnyPrepItems = Object.keys(prepItems).some(day => prepItems[day] && prepItems[day].length > 0);

  // Count total days with prep items
  const daysWithPrepItems = days.filter(day => prepItems[day] && prepItems[day].length > 0).length;

  return (
    <Modal show={show} onHide={onHide} size="lg" className="weekly-prep-list-modal">
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-5">
          <FontAwesomeIcon icon={faKitchenSet} className="text-warning me-2" />
          Weekly Prep List
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-2">
        {!hasAnyPrepItems ? (
          <div className="text-center text-muted py-3">
            <FontAwesomeIcon icon={faKitchenSet} size="2x" className="mb-2 text-secondary" />
            <p className="mb-0">No ingredients need prep this week!</p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                Ingredients to prep ({daysWithPrepItems} days)
              </small>
              <div>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 me-2 text-decoration-none"
                  onClick={() => toggleAllDays(false)}
                >
                  <FontAwesomeIcon icon={faExpandAlt} className="me-1" />
                  Expand All
                </Button>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 text-decoration-none"
                  onClick={() => toggleAllDays(true)}
                >
                  <FontAwesomeIcon icon={faCompressAlt} className="me-1" />
                  Collapse All
                </Button>
              </div>
            </div>
            <div className="prep-list-days">
              {days.map(day => {
                const dayItems = prepItems[day] || [];
                if (dayItems.length === 0) return null;

                return (
                  <div key={day} className="prep-day-section mb-2">
                    <div 
                      className={`day-header d-flex justify-content-between align-items-center py-2 px-2 rounded cursor-pointer ${day === today ? 'today-highlight' : ''}`}
                      onClick={() => toggleDay(day)}
                    >
                      <h6 className="mb-0 fw-bold">
                        {day} 
                        {day === today && <span className="badge bg-primary ms-2 small">Today</span>}
                        <small className="text-muted ms-1">({dayItems.length})</small>
                      </h6>
                      <FontAwesomeIcon 
                        icon={collapsedDays[day] ? faChevronDown : faChevronUp} 
                        className="text-muted"
                      />
                    </div>
                    <Collapse in={!collapsedDays[day]}>
                      <div>
                        {(() => {
                          // Group items by prep type
                          const groupedByType = dayItems.reduce((groups, item) => {
                            // Smart prep type detection - prefer default_prep_type when prep_type is just the migration default
                            let prepType;
                            if (item.prep_type && item.prep_type !== 'chop') {
                              prepType = item.prep_type;
                            } else if (item.default_prep_type) {
                              prepType = item.default_prep_type;
                            } else if (item.prep_type) {
                              prepType = item.prep_type;
                            } else {
                              prepType = 'chop';
                            }
                            
                            if (!groups[prepType]) {
                              groups[prepType] = [];
                            }
                            groups[prepType].push(item);
                            return groups;
                          }, {});
                          
                          const typeLabels = {
                            chop: 'Chop',
                            mix: 'Mix',
                            measure: 'Measure',
                            other: 'Other'
                          };
                          
                          // Define the order of prep types
                          const prepTypeOrder = ['chop', 'mix', 'measure', 'other'];
                          
                          // Sort entries by the defined order
                          const sortedEntries = Object.entries(groupedByType).sort(([a], [b]) => {
                            const indexA = prepTypeOrder.indexOf(a);
                            const indexB = prepTypeOrder.indexOf(b);
                            return indexA - indexB;
                          });
                          
                          return sortedEntries.map(([prepType, items]) => {
                            // For mix items, group by color
                            if (prepType === 'mix') {
                              const colorGroups = items.reduce((groups, item) => {
                                const color = item.color_tag || 'no-color';
                                if (!groups[color]) {
                                  groups[color] = [];
                                }
                                groups[color].push(item);
                                return groups;
                              }, {});
                              
                              // Filter out color groups that have only one item
                              // We need at least 2 items with the same color to mix
                              const validColorGroups = Object.entries(colorGroups).filter(([color, colorItems]) => {
                                // Count total unique ingredients with this color
                                // (considering that one ingredient might appear in multiple recipes)
                                const uniqueIngredients = new Set(
                                  colorItems.map(item => `${item.ingredient_id}-${item.custom_ingredient}`)
                                );
                                return uniqueIngredients.size >= 2;
                              });
                              
                              // If no valid color groups, skip this prep type
                              if (validColorGroups.length === 0) {
                                return null;
                              }
                              
                              const prepTypeKey = `${day}-${prepType}`;
                              const isPrepTypeCollapsed = collapsedPrepTypes[prepTypeKey] || false;
                              
                              return (
                                <div key={prepType} className="mb-2">
                                  <div 
                                    className="prep-type-header text-muted text-uppercase mb-2 ms-3 me-2 d-flex justify-content-between align-items-center"
                                    onClick={() => togglePrepType(day, prepType)}
                                  >
                                    <span>{typeLabels[prepType] || prepType}</span>
                                    <FontAwesomeIcon 
                                      icon={isPrepTypeCollapsed ? faChevronDown : faChevronUp} 
                                      className="text-muted"
                                      size="sm"
                                    />
                                  </div>
                                  <Collapse in={!isPrepTypeCollapsed}>
                                    <div>
                                  {validColorGroups.map(([color, colorItems]) => (
                                    <div key={color} className="mb-2">
                                      <ul className="list-unstyled ps-3 mb-0">
                                        {colorItems.map((item, index) => (
                                          <li key={`${item.ingredient_id}-${item.custom_ingredient}-${index}`} className="py-1 d-flex align-items-start">
                                            <div className="d-flex align-items-start flex-grow-1">
                                              <div 
                                                className="color-indicator me-2 mt-1 flex-shrink-0" 
                                                style={{ 
                                                  width: '12px', 
                                                  height: '12px', 
                                                  backgroundColor: color !== 'no-color' ? color : '#ccc',
                                                  borderRadius: '2px',
                                                  border: '1px solid #ddd'
                                                }}
                                                title={color !== 'no-color' ? 'Mix items with this color together' : 'No color assigned'}
                                              />
                                              <FontAwesomeIcon icon={faKitchenSet} className="text-warning mt-1 me-2 flex-shrink-0 small" />
                                              <div className="flex-grow-1 small">
                                                <div>
                                                  <strong>{formatQuantity(item.total_qty)}</strong>{" "}
                                                  {item.unit_name} <strong>{item.ingredient_name}</strong>{item.suffix && (
                                                    <span className="text-primary fw-bold"> ({item.suffix})</span>
                                                  )}
                                                  {item.section_name && (
                                                    <span className="text-info"> ({item.section_name})</span>
                                                  )}
                                                </div>
                                                <div className="text-muted" style={{fontSize: '0.8rem'}}>
                                                  {item.recipes.map((recipe, idx) => (
                                                    <span key={idx}>
                                                      ({formatQuantity(recipe.qty)}) {recipe.name}
                                                      {recipe.section_name && <span className="text-info"> - {recipe.section_name}</span>}
                                                      {idx < item.recipes.length - 1 && ", "}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                    </div>
                                  </Collapse>
                                </div>
                              );
                            }
                            
                            // Regular prep types (non-mix)
                            const prepTypeKey = `${day}-${prepType}`;
                            const isPrepTypeCollapsed = collapsedPrepTypes[prepTypeKey] || false;
                            
                            return (
                              <div key={prepType} className="mb-2">
                                <div 
                                  className="prep-type-header text-muted text-uppercase mb-2 ms-3 me-2 d-flex justify-content-between align-items-center"
                                  onClick={() => togglePrepType(day, prepType)}
                                >
                                  <span>{typeLabels[prepType] || prepType}</span>
                                  <FontAwesomeIcon 
                                    icon={isPrepTypeCollapsed ? faChevronDown : faChevronUp} 
                                    className="text-muted"
                                    size="sm"
                                  />
                                </div>
                                <Collapse in={!isPrepTypeCollapsed}>
                                  <div>
                                <ul className="list-unstyled ps-3 mb-0">
                                  {items.map((item, index) => (
                                  <li key={`${item.ingredient_id}-${item.custom_ingredient}-${index}`} className="py-1 d-flex align-items-start">
                                    <FontAwesomeIcon icon={faKitchenSet} className="text-warning mt-1 me-2 flex-shrink-0 small" />
                                    <div className="flex-grow-1 small">
                                      <div>
                                        <strong>{formatQuantity(item.total_qty)}</strong>{" "}
                                        {item.unit_name} <strong>{item.ingredient_name}</strong>{item.suffix && (
                                          <span className="text-primary fw-bold"> ({item.suffix})</span>
                                        )}
                                        {item.section_name && (
                                          <span className="text-info"> ({item.section_name})</span>
                                        )}
                                      </div>
                                      <div className="text-muted" style={{fontSize: '0.8rem'}}>
                                        {item.recipes.map((recipe, idx) => (
                                          <span key={idx}>
                                            ({formatQuantity(recipe.qty)}) {recipe.name}
                                            {recipe.section_name && <span className="text-info"> - {recipe.section_name}</span>}
                                            {idx < item.recipes.length - 1 && ", "}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                                </ul>
                                  </div>
                                </Collapse>
                              </div>
                            );
                          }).filter(Boolean); // Remove any null entries (mix sections with only 1 item)
                        })()}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WeeklyPrepListModal;